import React, { useState } from "react";
import axios from "axios";
import { Instagram, Linkedin, Twitter, Facebook, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || subscribing) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Welcome to the list. Expect quiet, useful emails.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer
      id="footer"
      data-testid="site-footer"
      className="relative bg-[#050505] border-t border-white/10"
    >
      {/* Big CTA */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24 py-24 md:py-32">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
            <h3 className="font-display text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tighter max-w-4xl">
              Let's build something
              <br />
              <em className="gold-text-gradient not-italic">exceptional</em>.
            </h3>
            <a
              href="#contact"
              data-testid="footer-cta"
              className="shine inline-flex items-center gap-3 px-8 py-5 text-sm font-semibold gold-bg-gradient text-black hover:opacity-90 transition-opacity"
            >
              Start a Project
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <a href="#top" className="flex items-center gap-3 mb-6">
              <span className="relative inline-flex h-10 w-10 items-center justify-center">
                <span className="absolute inset-0 rounded-full gold-bg-gradient"></span>
                <span className="relative font-display text-black text-xl font-bold">
                  A
                </span>
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-display text-white text-xl">Acnira</span>
                <span className="text-[10px] uppercase tracking-[0.32em] text-white/50">
                  Web Developers
                </span>
              </span>
            </a>
            <p className="text-white/55 leading-relaxed max-w-md">
              A senior-led independent digital studio in Ancoats, Manchester. Building
              websites, apps and brands for ambitious UK businesses since 2016.
            </p>

            <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-8 max-w-md">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                The studio dispatch · monthly
              </label>
              <div className="flex border border-white/15 focus-within:border-gold transition-colors">
                <input
                  type="email"
                  required
                  data-testid="newsletter-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.co.uk"
                  className="flex-1 bg-transparent px-4 py-3 text-white placeholder-white/30 outline-none text-sm"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={subscribing}
                  className="px-6 text-sm font-medium gold-bg-gradient text-black hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {subscribing ? "…" : "Subscribe"}
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
              Studio
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="#about" className="hover:text-gold transition-colors">About</a></li>
              <li><a href="#portfolio" className="hover:text-gold transition-colors">Work</a></li>
              <li><a href="#why" className="hover:text-gold transition-colors">Why Acnira</a></li>
              <li><a href="#contact" className="hover:text-gold transition-colors">Contact</a></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
              Services
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="#services" className="hover:text-gold transition-colors">Websites</a></li>
              <li><a href="#services" className="hover:text-gold transition-colors">Apps</a></li>
              <li><a href="#services" className="hover:text-gold transition-colors">Marketing</a></li>
              <li><a href="#services" className="hover:text-gold transition-colors">Branding</a></li>
              <li><a href="#services" className="hover:text-gold transition-colors">SEO</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
              Get in touch
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <a href="mailto:info@acnira.co.uk" className="hover:text-gold transition-colors">
                  info@acnira.co.uk
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/447767954568"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gold transition-colors"
                >
                  +44 07767 954568 · WhatsApp
                </a>
              </li>
              <li className="text-white/55">
                The Works, 33 Withy Grove
                <br />
                Manchester M4 2BJ
              </li>
            </ul>

            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Instagram, href: "https://instagram.com", label: "instagram" },
                { Icon: Linkedin, href: "https://linkedin.com", label: "linkedin" },
                { Icon: Twitter, href: "https://twitter.com", label: "twitter" },
                { Icon: Facebook, href: "https://facebook.com", label: "facebook" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${label}`}
                  aria-label={label}
                  className="h-10 w-10 flex items-center justify-center border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {year} Acnira Web Developers Ltd. Registered in England & Wales.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-gold transition-colors">Privacy</a>
            <a href="#" className="hover:text-gold transition-colors">Terms</a>
            <a href="#" className="hover:text-gold transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
